/* eslint-disable camelcase */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEqual, countBy, keyBy, orderBy, get } from 'lodash';
import cns from 'classnames';
import HTML5Backend from 'react-dnd-html5-backend';
import { DragDropContext } from 'react-dnd';
import DragCategoryResult from '../../../client/components/DragCategoryResult';
import BottomCategoryResult from '../../../client/components/BottomCategoryResult';
import RichTextDisplay from '../../components/RichTextDisplay';
import WorkspaceSlideHeader from './WorkspaceSlideHeader';
import TrackPlayerSlideComponent from '../../hocs/TrackPlayerSlideComponent';
import VerbHeading from '../../components/VerbHeading';

class ThisOrThatResultSlide extends Component {
    static propTypes = {
        completedData: PropTypes.object,
        onChange: PropTypes.func,
        content: PropTypes.shape({
            resultsToShow: PropTypes.number,
            isDraggable: PropTypes.bool,
            showImage: PropTypes.bool,
            description: PropTypes.string
        }).isRequired,
        name: PropTypes.string.isRequired,
        canMoveToNext: PropTypes.func,
        currentUser: PropTypes.object.isRequired,
        abQuestionContent: PropTypes.object.isRequired,
        abQuestionData: PropTypes.object.isRequired,
        connectDragSource: PropTypes.func,
        connectDropTarget: PropTypes.func,
        nextSectionName: PropTypes.string,
        sectionId: PropTypes.string,
        sectionItemId: PropTypes.string,
        hideBookmark: PropTypes.bool,
        sendWorkspaceSegementEvent: PropTypes.func,
        isRefSheet: PropTypes.bool,
    };

    constructor(props) {
        super(props);

        this.state = {
            categoryLookup: this._generateCategoryLookupById(),
            sortedCategories: this._sortCategories(),
            userResults: get(props, 'abQuestionData.data.userResults', null)
        };
    }

    /**
     * Returns a dictionary of categories that are keyed by the categoryId
     * @return {Object}
     * @private
     */
    _generateCategoryLookupById = () => {
        const { abQuestionContent } = this.props;

        return keyBy(abQuestionContent.categories, 'id');
    };

    /**
     * Parses the results of the user's A|B result and displays it for consumption
     * @param {Object} abQuestionData
     * @param {Object} abQuestionData.data
     * @param {number} resultsToShow
     * @private
     */
    _sortCategories = () => {
        const { abQuestionData } = this.props;
        const categoryLookup = this._generateCategoryLookupById();

        // count the result answers

        if (!abQuestionData.data) {
            return null;
        }
        const countedResult = countBy(Object.values(get(abQuestionData, 'data', {})));

        const lookupKeys = Object.keys(categoryLookup);

        // convert key/value lookup into an array of objects
        const countedArray = lookupKeys.map((key) => ({ categoryId: key, count: countedResult[key] || 0 }));

        // sort objects by count
        const sortedResult = orderBy(countedArray, 'count', 'desc');

        // pull the top N results to display
        return sortedResult;
    };

    componentDidMount() {
        const { completedData, onChange, canMoveToNext } = this.props;
        const isCompleted = true;

        const isAnswered = true;
        onChange(get(completedData, 'data', null), isCompleted, isAnswered);
        canMoveToNext(true);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!isEqual(nextProps.abQuestionContent, this.props.abQuestionContent)) {
            this.props.canMoveToNext(true);
            this.setState({
                categoryLookup: this._generateCategoryLookupById(),
                sortedCategories: this._sortCategories()
            });
        }
    }

    moveCard = (dragIndex, hoverIndex) => {
        const { userResults, sortedCategories } = this.state;
        const { abQuestionData } = this.props;

        const dragArray = userResults ? userResults.slice() : sortedCategories.slice();

        const dragkey = dragArray[dragIndex];

        dragArray.splice(dragIndex, 1);
        dragArray.splice(hoverIndex, 0, dragkey);

        this.setState({
            userResults: dragArray
        });

        const { data } = abQuestionData;
        data.userResults = this.state.userResults;
        data.calcedResults = sortedCategories;
        const params = {
            ...abQuestionData,
            data
        };
        this.props.onChange(params, true);
    };

    render() {
        const {
            name,
            nextSectionName,
            content,
            connectDragSource,
            currentUser,
            connectDropTarget,
            sectionId,
            sectionItemId,
            hideBookmark,
            sendWorkspaceSegementEvent,
            isRefSheet = false,
        } = this.props;

        const draggable = content.isDraggable;
        const categoriesToDisplay = this.state.userResults || this.state.sortedCategories;
        if (!categoriesToDisplay) return null;

        // render display
        const topResults = categoriesToDisplay
            .slice(0, content.resultsToShow)
            .map(({ categoryId }, index) => (
                <DragCategoryResult
                    key={categoryId}
                    className={'col-12 col-md-12'}
                    moveCard={this.moveCard}
                    categoryLookup={this.state.categoryLookup}
                    categoryId={categoryId}
                    canDrag={draggable}
                    currentIndex={index}
                    connectDragSource={connectDragSource}
                    connectDropTarget={connectDropTarget}
                />
            ));
        const bottomResults = categoriesToDisplay
            .slice(content.resultsToShow)
            .map(({ categoryId }, index) => (
                <BottomCategoryResult
                    key={categoryId}
                    className={'col-sm-12'}
                    moveCard={this.moveCard}
                    displayImage={content.showImage}
                    categoryLookup={this.state.categoryLookup}
                    categoryId={categoryId}
                    canDrag={draggable}
                    currentIndex={index + content.resultsToShow}
                    connectDragSource={connectDragSource}
                    connectDropTarget={connectDropTarget}
                />
            ));
        return (
            <div>
                <WorkspaceSlideHeader
                    headerText={name}
                    currentSectionItemId={sectionItemId}
                    currentSectionId={sectionId}
                    currentUser={currentUser}
                    hideBookmark={hideBookmark}
                    isRefSheet={isRefSheet}
                />
                <div className={cns('thisOrThatResultSection', 'playerContent')}>
                    {content.description ? (
                        <RichTextDisplay
                            className={'playerDescription'}
                            htmlToDisplay={content.description}
                            currentUser={currentUser}
                            nextSectionName={nextSectionName}
                        />
                    ) : null}
                    <div className={cns('col-12', { 'col-md-9': bottomResults.length })}>
                        <VerbHeading
                            hNumber={4}
                            messageId={'WORKSPACE_THIS_OR_THAT_RESULTS'}
                            defaultMessage={'Your Results'}
                        />
                        {topResults}
                    </div>
                </div>
            </div>
        );
    }
}

export const ThisOrThatResultSlideComponent = DragDropContext(HTML5Backend)(ThisOrThatResultSlide);
export default TrackPlayerSlideComponent(ThisOrThatResultSlideComponent);
